import React, { memo, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid, Menu, Typography } from '@mui/material';
import { AddressFind, Cell, Order, PointOfSale } from '@m4a3/api-client';
import { API } from 'aws-amplify';
import {
  usePopupState,
  bindTrigger,
  bindMenu,
  bindContextMenu
} from 'material-ui-popup-state/hooks';
import { OrderCardContext } from './OrderCardContext';
import { useDispatch, useSelector } from 'react-redux';
import {
  setHoverOrder,
  setHoverTeam,
  setRouteConfirmationOrder,
  setSelectedOrderId,
  updateTagsThunk
} from 'actions/orderActions';
import {
  selectCells,
  selectHoverTeam,
  selectIsOrderSelected,
} from '../../../selectors/ordersSelector';
import { flyingStatuses, inactiveStatuses, pecanSquareCellId, espooCellId } from '../../../constants';
import { useAppSelector } from 'hooks/redux';
import { DeparturePadTag } from './DeparturePadTag';
import { ArrivalPadTag } from './ArrivalPadTag';
import DueIn from './DueIn';
import { LastUpdated } from './LastUpdated';
import useInterval from 'utils/useInterval';
import { getStatusData } from '../../../services/order-service';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import { LoadingProgressText } from './LoadingProgressText';
import { areOrdersEqual, getElementForDropPointStatus, getOrderActionCardData, getShortIdForOrder, isDueIn30Mins, isOrderLate, renderOrderKm } from '../../../utils/orderUtils';
import OrderCardAction from './OrderCardAction';
import { convertUtcDateStringForTimezone, convertUtcTimeStringForTimezone, defaultTimezone, droneDeliveryDurationMinutes, isoStringToDateTimeObject, shortDateStringOptions } from '../../../utils/date-utils';
import { selectPointOfSales } from '../../../selectors/adminSelector';
import { selectOverweightColorPairingsForOrder } from '../../../selectors/ordersSelector';
import typography from '../../../theme/typography';
import HighlightedShortId from '../../../components/HighlightedShortId/HighlightedShortId';
import { getCollectorStatusAltTag, getEmojiForCollectorStatus } from '../types/CollectorStatus';
import OverweightOrderBanner from './OverweightOrderBanner';
import PinDropIcon from '@mui/icons-material/PinDrop';
import { getLocationColor } from '../../../utils/locationColors';
import { newNotification } from '../../../actions/uiActions';

const MISSION_STATUS = {
  PROPOSED: { text: 'Proposed', color: 'orange' },
  HOLD: { text: 'Hold', color: 'green' },
  IN_PROGRESS: { text: 'In Progress', color: 'red' },
  COMPLETED: { text: 'Complete', color: 'gray' },
  MANUAL: { text: 'Aborted', color: 'red' },
  ABORTED: { text: 'Aborted', color: 'red' }
};

const iconSize = 18;
const iconStyle = { height: iconSize, width: iconSize };

const DeprecatedOrderCardComponent = (

  props: {
    order: Order,
    isBackGardenDelivery?: boolean,
    addressFind?: AddressFind,
    onClickShowSuggestedDropModalForOrder?: (orderId: string) => void,
    onClickInvalidateDropLocation?: (orderId: string, locationId: string) => void,
    aircraftData?: any,
    onOpenRouteConfirmation?: (order: Order) => void,
    updateLandingZoneForOrder?: (orderId: string, landingZoneId: string) => void,
    landingZones?: any[],
    pads: any[],
    openSuggestModal?: () => void
  }) => {

  const dispatch = useDispatch();

  const hoverTeam = useSelector(selectHoverTeam);
  const cells = useSelector(selectCells);
  const pointOfSales = useSelector(selectPointOfSales);
  const linkedOrdersForOverweightOrders = useAppSelector(state => selectOverweightColorPairingsForOrder(state, props.order.orderShortId));

  type DeprecatedOrderCardData = {
    buttonText: string;
    iconComponent: JSX.Element;
  }

  const isSelected = useAppSelector(state =>
    selectIsOrderSelected(state, props.order)
  );
  const overDueRef = useRef(false);
  const dueIn30MinsRef = useRef(false);
  const [overDue, setOverDue] = useState(isOrderLate(props.order));
  const [pos, setPos] = useState<PointOfSale | null>(null);
  const [dueIn30Mins, setDueIn30Mins] = useState(isDueIn30Mins(props.order));
  const [deprecatedOrderCardData, setDeprecatedOrderCardData] = useState<DeprecatedOrderCardData  | null>(null);
  
  useInterval(function() {
    if (isOrderLate(props.order) && overDueRef.current === false) {
      overDueRef.current = isOrderLate(props.order);
      setOverDue(true);
    }
    if (isDueIn30Mins(props.order) && dueIn30MinsRef.current === false) {
      dueIn30MinsRef.current = isDueIn30Mins(props.order);
      setDueIn30Mins(true);
    }
  }, 1000);

  useEffect(() => {
    if (props?.order) {
      setDeprecatedOrderCardData(getOrderActionCardData(props.order));
    }
  }, [props.order, props.addressFind]);

  useEffect(() => {
    if (pointOfSales) {
      setPos(pointOfSales?.find((pos) => pos.id === props.order.posId || parseInt(props.order?.externalProviderId) === pos?.fdStoreId));
    }
  }, [pointOfSales]);

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'full-menu'
  });
  const popupStateTeam = usePopupState({
    variant: 'popover',
    popupId: 'demo-team'
  });
  const popupStateAircraft = usePopupState({
    variant: 'popover',
    popupId: 'demo-aircraft'
  });
  const popupStateLane = usePopupState({
    variant: 'popover',
    popupId: 'demo-lane'
  });
  const popupStateDropPoint = usePopupState({
    variant: 'popover',
    popupId: 'drop-point-popup'
  });
  const popupStateArrivalPad = usePopupState({
    variant: 'popover',
    popupId: 'arrival-pad'
  });
  const popupStateLandingZone = usePopupState({
    variant: 'popover',
    popupId: 'landing-zone'
  });

  const shouldShowOverweightOrderUI = (): boolean => {
    if (linkedOrdersForOverweightOrders?.linkedOverweightOrderIds && linkedOrdersForOverweightOrders?.linkedOverweightOrderIds.length > 0) {
      return true;
    }

    return false;
  }

  const activeMissionStatus = (MISSION_STATUS as any)[
    // @ts-ignore
    props.order.mission?.status || props.order.missionStatus
  ];

  const missionReadyFoodNot =
    (props.order.mission?.status || (props.order as any).missionStatus) ===
      'HOLD' && props.order.status !== 'READY';

  const selectOrder = () => {

    if (!props.order.eircode) {
      dispatch(newNotification({
        variant: 'error',
        message: 'BLOCKING ERROR: Order has no placeId - do not proceed with order'
      }));
    }

    dispatch(setSelectedOrderId(props.order.id));
  };

  const stop = (func: (event: SyntheticEvent<any>) => void) => (
    event: SyntheticEvent<any>
  ) => {
    func(event);
    event.stopPropagation();
  };

  const click = (func: (event: SyntheticEvent<any>) => void) => (
    event: SyntheticEvent<any>
  ) => {
    func(event);
    event.stopPropagation();
  };

  const getTimezoneForOrderCell = (order: Order): string => {
    const orderCellId = order?.cellId;
    const matchingCell: Cell = cells?.find((c: Cell) => c.id === orderCellId);
    return matchingCell?.timezone || defaultTimezone;
  };

  const statusData = getStatusData(props.order.status);
  const IconComponent = statusData.icon;
  const team = props.order?.tags?.find(tag => tag.includes('TEAM'));
  const lane = props.order?.tags?.find(tag => tag.includes('LANE'));
  const priority = props.order?.tags?.includes('PRIORITY');
  const isTeamHovered = team && team === hoverTeam;
  const newTeamState = (status: string) => {
    if (status === 'NEW') {
      return ''; // shows loading indicator
    } else if (status === 'ON_THE_WAY') {
      return '🚗';
    } else if (status === 'ARRIVED') {
      return '🏡';
    } else if (status === 'DROP_SAFE') {
      return '✅';
    } else if (status === 'DROP_SUGGESTED') {
      if (
        props.order?.suggestedDrop &&
        props.order?.suggestedDrop !== '0.0,0.0'
      )
        return '📍';
      return '';
    } else if (status === 'DROP_REJECTED') {
      return '❌';
    } else if (status === 'AWAITING_DROP') {
      return '✅';
    } else if (status === 'COMPLETE') {
      return '✅';
    }
    return '';
  };

  const getTakeOffTimeForOrder = (expectedDeliveryDate: string): string => {
    const expectedTakeoffTime = isoStringToDateTimeObject(expectedDeliveryDate, getTimezoneForOrderCell(props.order))
      .minus({minutes: droneDeliveryDurationMinutes})
      .toJSDate()
      .toISOString();
    return convertUtcTimeStringForTimezone(expectedTakeoffTime, getTimezoneForOrderCell(props.order));
  };

  const getDateForOrderSlot = (expectedDeliveryDate: string): string => {
    return convertUtcDateStringForTimezone(expectedDeliveryDate, getTimezoneForOrderCell(props.order), shortDateStringOptions);
  };

  const mouseEnterOrder = () => {
    if (props.order) {
      dispatch(setHoverOrder(props.order?.id));
    }
  };

  const getOrderWeightInKg = () => {
    if (props.order?.actualWeight) {
      return props.order?.actualWeight;
    }

    if ((props.order as any)?.totalWeight) {
        return (props.order as any)?.totalWeight / 1000;
    }
  };

  const selectedLandingZoneId = (props.order as any).landingZoneId as string | undefined;
  const dropdownLandingZones = useMemo(() => {
    const zones = (props.landingZones && props.landingZones.length > 0)
      ? props.landingZones
      : [{ id: selectedLandingZoneId, name: props.order.landingZoneName }];

    return zones.slice().sort((a: any, b: any) => {
      const aSelectedLZ = a?.id === selectedLandingZoneId;
      const bSelectedLZ = b?.id === selectedLandingZoneId;
      if (aSelectedLZ && !bSelectedLZ) return -1;
      if (bSelectedLZ && !aSelectedLZ) return 1;
      return (a?.name || '').localeCompare(b?.name || '');
    });
  }, [props.landingZones, selectedLandingZoneId, props.order.landingZoneName]);

  const handleSelectLandingZone = useCallback(
    (zoneId: string) => (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (zoneId && zoneId !== selectedLandingZoneId && props.updateLandingZoneForOrder) {
        props.updateLandingZoneForOrder(props.order.id, zoneId);
        popupStateLandingZone.close();
      }
    },
    [props.updateLandingZoneForOrder, props.order.id, selectedLandingZoneId]
  );


  const mouseExitOrder = () => {
    dispatch(setHoverOrder(null));
  };

  const mouseEnterTeam = () => {
    if (props.order) {
      if (hoverTeam === team) return;
      dispatch(setHoverTeam(team));
    }
  };

  const mouseExitTeam = () => {
    if (hoverTeam === null) return;
    dispatch(setHoverTeam(null));
  };

  const renderOrderCount = (count: number) => {
    if (!count) {
      return '🎉';
    } else if (count >= 10) {
      return `⭐️⭐️ ${count}`;
    } else if (count >= 1) {
      return `⭐️ ${count}`;
    }
    return '';
  };

  //Espoo deliveries are handled by wolt, so our drop locations need to be set differently, specifically using the link built within handlePinLocationPortal
  const handlePinLocationPortal = async () => {

    const order: any = props.order;
    if (!order || !order?.id || !order?.eircode) {
      return;
    }

    try {
      const addressFind: any = await API.get('api', `/addressfind/locationId/${order.eircode}`, {});
      const latitude = addressFind?.address_latitude ?? addressFind?.latitude; 
      const longitude = addressFind?.address_longitude ?? addressFind?.longitude; 
      if (!latitude || !longitude) {
        console.error('No coordinates found in addressfind data');
        return;
      }
      const orderCellId = order.cellId;
      const matchingCell = cells?.find(c => c.id === orderCellId);
      const cellName = matchingCell?.cellName || '';
      const locationId = order.eircode;
      const url = `https://manna-int.m4mga3.com/osrm-map.html?#lat=${latitude}&lng=${longitude}&cellName=${cellName}&eircode=${locationId}&orderId=${order.id}`;  
      window.open(
        url,
        'blank'
      );
    } catch (error) {
      console.error('Error fetching addressfind data:', error);
    }
  };

  const handleDeprecatedOrderCardAction = () => {
    const actionText = deprecatedOrderCardData?.buttonText?.toLowerCase();

    if (actionText === 'suggest delivery spot') {
      selectOrder();

      if (props.openSuggestModal) {
        props.openSuggestModal();
      }
      return;
    }

    if (actionText === 'load aircraft') {
      const newTags = [...(props.order?.tags || []), 'LOAD_NOW'];
      dispatch(updateTagsThunk(props.order.id, newTags));
      return;
    }

    if (props.order.cellId === espooCellId) {
      handlePinLocationPortal();
      return;
    }

    if (actionText === 'confirm route') {
      props.onOpenRouteConfirmation?.(props.order);
    }
  };

  const onClickInvalidateDropLocation = (orderId: string, locationId: string) => {
    props.onClickInvalidateDropLocation(orderId, locationId);
  }

  //Push espoo MCs to the url in handlePinLocationPortal for creating drop locations 
  const onClickShowSuggestedDropModalForOrder = (orderId: string) => {
    if (props.order.cellId === espooCellId) {
      handlePinLocationPortal();
    } else {
      props.onClickShowSuggestedDropModalForOrder(orderId);
    }
  }

  // Disable opening the drop location suggestion overlay on click
  // Keep a no-op handler to prevent bubbling to the card click
  const handleClickDropPointStatus = (event) => {
    event.stopPropagation();
  }

  // Helper function to determine if ArrivalPadTag should be enabled
  const shouldEnableArrivalPadTag = (): boolean => {
    // Enable if order is from JUSTEAT and selected landing pad is BT_A
    const isJustEatOrder = props.order.externalProviderName === 'JUSTEAT';
    
    if (!isJustEatOrder) {
      return false; // Keep disabled for all non-JUSTEAT orders
    }
    
    // For JUSTEAT orders, check if the selected landing pad is BT_A
    const selectedPadName = props.pads?.find(pad => pad.id === props.order.destinationLandingPadId)?.name;
    return selectedPadName === 'BT_A';
  };
  
  const getResendingOrderBadge = (): JSX.Element => {
    return (<div className='absolute top-0 right-1 z-40 overflow-visible'>
      <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700">
        Resending
      </span>
    </div>); 
  };
  
  const getDeprecatedOrderCardBorder = (): string => {
    if (isSelected) {
      return `3px solid ${statusData.colour}`;
    }
    
    if (overDue && !inactiveStatuses.includes(props.order.status) && props.order.status !== 'CREATED') {
      return '1px solid #ef4444';
    }
    
    return '1px solid #d1d5db';
  };
  
  return (
    <Box
      {...bindContextMenu(popupState)}
      onMouseEnter={mouseEnterOrder}
      onMouseLeave={mouseExitOrder}
      onClick={selectOrder}
      className={`relative cursor-pointer flex flex-col justify-between rounded-lg ${props.order.status === 'CREATED' ? 'animate-pulse' : ''}`}
      sx={{
        width: 267,
        bgcolor: missionReadyFoodNot ? '#EAAC30' : '#FFFFFF',
        border: getDeprecatedOrderCardBorder(),
        borderRadius: 2,
      }}
    >
      {shouldShowOverweightOrderUI() ? (
        <OverweightOrderBanner overweightInfo={linkedOrdersForOverweightOrders}/>
      ):(
        null
      )}
      {props.order.originalOrderId ? getResendingOrderBadge() : null}
      <div className={`flex flex-col w-full py-3 px-2`}>
        <div className="flex flex-row">
          <div className="flex flex-col" style={{ width: 180 }}>
            <Box className="flex flex-row" sx={{ height: 18 }}>
              <Box className="flex items-center justify-center">
                {/* @ts-ignore */}
                <IconComponent
                  stroke={statusData.colour}
                  style={{
                    ...iconStyle,
                    color: statusData.colour,
                    alignSelf: 'center'
                  }}
                />
              </Box>
              <Box className="flex items-center" sx={{ height: 18 }} pl={1}>
                <h3 className={'text-nowrap'} style={{
                  color: statusData.colour,
                  fontWeight: 700,
                  fontSize: 12
                }}>
                  {getStatusData(props.order.status)?.text}
                  {activeMissionStatus ? (
                    <>
                      <span className={'inline text-black font-semibold'} style={{ fontSize: 12 }}>
                        {' • '}
                      </span>
                      <span className={'inline font-semibold'} style={{ color: activeMissionStatus?.color, fontSize: 12 }}>
                        {activeMissionStatus?.text}{' '}
                      </span>
                    </>
                  ) : null}
                </h3>
              </Box>
            </Box>
            <Box className="flex items-center text-center mt-1.5">
              <HighlightedShortId orderShortId={getShortIdForOrder(props.order)} headingLevel='h6' />
            </Box>
            <Box className="flex" sx={{ height: 18 }}>
              <Typography variant="h6" style={{ fontSize: 12, fontWeight: 500 }}>
                Takeoff at{' '}
                <Typography component="span" display="inline" variant="h6" style={{ fontSize: 15, fontWeight: 600 }}>
                  {getTakeOffTimeForOrder(props.order.expectedDeliveryDate)}{' '}
                </Typography>
                {getDateForOrderSlot(props.order.expectedDeliveryDate)}
              </Typography>
            </Box>
            <Box className="flex" sx={{ height: 18, width: '100%' }}>
              <Typography variant="h6" style={{ fontSize: 12, fontWeight: 500, color: '#6E7191' }}>
                {' ' + renderOrderCount(props.order.orderCount)} •{' '}
                {renderOrderKm(
                  parseFloat(
                    (props.order.missionKmDistance as unknown) as string
                  )
                )}
              </Typography>
            </Box>
          </div>
          <Box className="flex flex-col" sx={{ width: 48, marginLeft: 'auto' }} justifyContent="space-between">
            <Box className="flex" sx={{ width: 48, height: 48, border: '1px solid #D9DBE9', borderRadius: 2, overflow: 'hidden' }}>
              {pos ? (
                <Box className="block relative overflow-hidden" sx={{ width: 48, height: 48 }}>
                  <img
                    alt="store logo"
                    src={pos.logo}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%'
                    }}
                  />
                </Box>
              ) : null}
            </Box>
            <Box className="flex" height={48} width={48}>
              <Typography style={{ fontSize: 10, lineHeight: 1 }}>
                {props.order.outletName}
              </Typography>
            </Box>
          </Box>
        </div>

        <Box className="flex" sx={{ height: 32, marginTop: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <DeparturePadTag popupState={popupStateAircraft} departurePadId={props.order.originLandingPadId ?? null} />
            </Grid>
            <Grid item xs={1}>
              <div className="line-horizontal arrow-right"></div>
            </Grid>
            <Grid item xs={1}>
              <PinDropIcon color="success" />
            </Grid>
            <Grid item xs={1}>
              <div className="line-horizontal arrow-right"></div>
            </Grid>
            <Grid item xs={4}>
              <ArrivalPadTag 
                popupState={popupStateArrivalPad} 
                destinationPad={props.order.destinationLandingPadId ?? null} 
                originPad={props.order.originLandingPadId ?? null}
                cellId={props.order.cellId}
                disabled={!shouldEnableArrivalPadTag()} // Enable for JUSTEAT orders with BT_A landing pad, otherwise keep disabled
              />
            </Grid>
          </Grid>
        </Box>

        <Box className="flex" sx={{ height: 32, marginTop: 1 }}>
          {(props.order.cellId === pecanSquareCellId && props.order.teamStatus !== undefined) ? (
            <Box
              {...bindTrigger(popupStateTeam)}
              onClick={click(bindTrigger(popupStateTeam).onClick)}
              className="flex items-center justify-center px-0.5"
              bgcolor={isTeamHovered ? '#EAAC30' : 'white'}
              onMouseEnter={mouseEnterTeam}
              onMouseLeave={mouseExitTeam}
              sx={{ height: 32, borderRadius: 1, border: `1px ${team ? 'solid' : 'dashed'} ${team ? '#14142B' : '#D9DBE9'}` }}
              ml={1}
            >
              <span className='font-semibold text-nowrap'
                style={{
                  ...typography.body2,
                  color: team ? '#14142B' : 'gray'
                }}
              >
                  {team ? team?.replace('TEAM', '') : 'Team'}&nbsp;
              </span>
              <span className='font-semibold text-nowrap'
                style={{
                  ...typography.body2,
                  color: team ? '#14142B' : 'gray'
                }}
              >
                  {team ? ' ' : ''}
                  {team ? newTeamState(props.order.teamStatus) : ''}
              </span>
              {team && ['PENDING', 'NEW'].includes(props.order.teamStatus) ? (
                <LoadingSpinner size={20} />
              ) : null}
            </Box>
          ) : null}

          {getOrderWeightInKg() ? (
            <Box
              pl={0.5}
              className="flex items-center justify-center"
              bgcolor={'#FFFFFF'}
              sx={{ width: 48, height: 32, borderRadius: 1, border: `1px solid #14142B` }}
              ml={1}
            >
              <span className='font-semibold'
                style={{
                  ...typography.body2,
                  color: getOrderWeightInKg() > 2 ? '#ED0505' : 'black'
                }}
              >
                {getOrderWeightInKg().toFixed(2)}kg{' '}
              </span>
            </Box>
          ) : null}
          <Box
            onClick={handleClickDropPointStatus}
            className="flex items-center justify-center px-0.5 rounded-full h-8 w-8"
            bgcolor={'white'}
            sx={{ border: `1px solid #14142B` }}
            ml={1}
          >
            {getElementForDropPointStatus(props.order)}
          </Box>
          {getEmojiForCollectorStatus(props?.order?.collectorStatus ?? '') ? (
          <Box
            className="flex items-center justify-center px-0.5 rounded-full h-8 w-8 relative"
            bgcolor={'white'}
            sx={{ 
              border: `1px solid #14142B`,
              '&:hover .collector-info': {
                display: 'flex'
              }
            }}
            ml={1}
          >
            {getEmojiForCollectorStatus(props?.order?.collectorStatus ?? '')}
            
            {(props.order.collectorId && props.order.collectorId !== 'NO_COLLECTOR') && (
              <Box 
                className="collector-info absolute hidden"
                sx={{ 
                  top: '100%', 
                  left: '50%', 
                  transform: 'translateX(-50%)', 
                  mt: 1,
                  p: 1,
                  bgcolor: 'white',
                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                  borderRadius: '4px',
                  zIndex: 10
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <svg 
                    width="50" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginRight: '6px' }}
                  >
                    <path d="M19 8L22 5M22 5L19 2M22 5L16 5M22 12V17.2C22 18.8802 22 19.7202 21.673 20.362C21.3854 20.9265 20.9265 21.3854 20.362 21.673C19.7202 22 18.8802 22 17.2 22H6.8C5.11984 22 4.27976 22 3.63803 21.673C3.07354 21.3854 2.6146 20.9265 2.32698 20.362C2 19.7202 2 18.8802 2 17.2V6.8C2 5.11984 2 4.27976 2.32698 3.63803C2.6146 3.07354 3.07354 2.6146 3.63803 2.32698C4.27976 2 5.11984 2 6.8 2H12M2.14551 19.9263C2.61465 18.2386 4.16256 17 5.99977 17H12.9998C13.9291 17 14.3937 17 14.7801 17.0769C16.3669 17.3925 17.6073 18.6329 17.9229 20.2196C17.9998 20.606 17.9998 21.0707 17.9998 22M14 9.5C14 11.7091 12.2091 13.5 10 13.5C7.79086 13.5 6 11.7091 6 9.5C6 7.29086 7.79086 5.5 10 5.5C12.2091 5.5 14 7.29086 14 9.5Z" stroke="#14142B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <Typography variant="body2" color="textSecondary">
                    {getCollectorStatusAltTag(props.order.collectorStatus)} ({props.order.collectorId})
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        ):(
          null
        )}
        
        {props.order.landingZoneName && (
          <Box
            {...bindTrigger(popupStateLandingZone)}
            className="flex items-center justify-center px-0.5 rounded-full h-8 w-8 relative"
            bgcolor={getLocationColor(props.order.landingZoneName.substring(0, 3))}
            sx={{ 
              border: `1px solid #14142B`
            }}
            ml={1}
          >
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
              {props.order.landingZoneName.substring(0, 3)}
            </Typography>
          </Box>
        )}
        </Box>
        {![...inactiveStatuses, ...flyingStatuses].includes(props.order.status) && (
          <LastUpdated modifiedDate={props.order.modifyDate} status={props.order.status} />
        )}
        {['RECEIVED_FROM_STORE', 'READY'].includes(props.order.status) && (
          <LoadingProgressText order={props.order} />
        )}
        {dueIn30Mins && ![...inactiveStatuses, ...flyingStatuses].includes(props.order.status) && (
            <DueIn key={`${getShortIdForOrder(props.order)}-duein`} expectedDeliveryDate={props.order.expectedDeliveryDate} takeOffTimeMs={props.order.takeOffTimeMs} id={getShortIdForOrder(props.order)} />
        )}
      </div>
      
      {(deprecatedOrderCardData?.buttonText && deprecatedOrderCardData?.iconComponent) ? <OrderCardAction onClick={handleDeprecatedOrderCardAction} buttonText={deprecatedOrderCardData.buttonText} iconComponent={deprecatedOrderCardData.iconComponent} /> : null}
      <Menu {...bindMenu(popupState)} onClose={stop(bindMenu(popupState).onClose)}>
        <OrderCardContext popupState={popupState} order={props.order} tags={props.order.tags} pads={props.pads} />
      </Menu>

      <Menu {...bindMenu(popupStateTeam)} onClose={stop(bindMenu(popupStateTeam).onClose)}>
        <OrderCardContext popupState={popupStateTeam} order={props.order} tags={props.order.tags} subMenu="team" pads={props.pads} />
      </Menu>

      <Menu {...bindMenu(popupStateAircraft)} onClose={stop(bindMenu(popupStateAircraft).onClose)}>
        <OrderCardContext 
          popupState={popupStateAircraft} 
          order={props.order} 
          tags={props.order.tags} 
          subMenu="aircraft"
          aircraftData={props.aircraftData}
          pads={props.pads}
        />
      </Menu>
      <Menu {...bindMenu(popupStateLane)} onClose={stop(bindMenu(popupStateLane).onClose)}>
        <OrderCardContext popupState={popupStateLane} order={props.order} tags={props.order.tags} subMenu="lane" pads={props.pads} />
      </Menu>
      <Menu {...bindMenu(popupStateDropPoint)} onClose={stop(bindMenu(popupStateDropPoint).onClose)}>
        <OrderCardContext
          popupState={popupStateDropPoint}
          order={props.order}
          tags={props.order.tags}
          subMenu="drop-point"
          onClickShowSuggestedDropModalForOrder={onClickShowSuggestedDropModalForOrder}
          onClickInvalidateDropLocation={onClickInvalidateDropLocation}
          pads={props.pads}/>
      </Menu>
      <Menu {...bindMenu(popupStateArrivalPad)} onClose={stop(bindMenu(popupStateArrivalPad).onClose)}>
        <OrderCardContext 
          popupState={popupStateArrivalPad} 
          order={props.order} 
          tags={props.order.tags} 
          subMenu="arrival-pad"
          pads={props.pads}
        />
      </Menu>
      <Menu {...bindMenu(popupStateLandingZone)} onClose={stop(bindMenu(popupStateLandingZone).onClose)}>
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 220, p: 1 }}>
          {dropdownLandingZones.map((zone: any) => {
            const zoneName: string = zone?.name || '';
            const zoneId: string = zone?.id;
            const abbr = (zoneName || '').substring(0, 3);
            const isSelected = zoneId === selectedLandingZoneId;

            return (
              <Box
                key={`lz-option-${zoneId}`}
                onClick={handleSelectLandingZone(zoneId)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1,
                  py: 1,
                  borderRadius: 1,
                  backgroundColor: isSelected ? '#EEF2FF' : 'white',
                  cursor: isSelected ? 'default' : 'pointer',
                  '&:hover': { backgroundColor: isSelected ? '#EEF2FF' : '#F3F4F6' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '9999px',
                    bgcolor: getLocationColor(abbr),
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 12,
                    lineHeight: 1,
                    mr: 1
                  }}>
                    {abbr}
                  </Box>
                  <Typography variant="body2" color="text.primary">{zoneName}</Typography>
                </Box>
                {isSelected ? (
                  <Typography variant="body2" color="primary">✓</Typography>
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Menu>
    </Box>
  );
};

export const DeprecatedOrderCard = memo(DeprecatedOrderCardComponent, areOrdersEqual);

